import type { ProductQuestionGroupQuestion } from "../client/generated";
import { z } from "zod";

/**
 * Convert a GUID with dots to a safe field name
 */
export const toSafeGuid = (guid: string) => guid.split(".").join("__dot__");

/**
 * Convert a GUID to a safe field name with answer- prefix
 */
export const toSafeField = (guid: string) => `answer-${toSafeGuid(guid)}`;

/**
 * Convert back to original field name with answer- prefix
 */
export const toOriginalField = (guid: string) => `answer-${guid}`;

/**
 * Build a Zod validation schema from product question group questions
 */
export function buildQuestionnaireSchema(questions: ProductQuestionGroupQuestion[]) {
  return z.object(
    Object.fromEntries(
      questions.map((q) => {
        const guid = q.product_question_group_question_guid;
        const fieldName = toSafeField(guid);
        let s: z.ZodTypeAny;

        if (q.question_type === "freetext") {
          const maxLen = typeof q.max_text_length === "number" && q.max_text_length > 0 ? q.max_text_length : 10000;
          s = z
            .string()
            .max(maxLen)
            .transform((v: string) => v.trim());
        } else if (q.question_type === "select_with_custom") {
          const maxLen = typeof q.max_text_length === "number" && q.max_text_length > 0 ? q.max_text_length : 10000;
          s = z
            .string()
            .max(maxLen)
            .transform((v: string) => v.trim());
        } else if (q.question_type === "number") {
          const minVal = typeof q.number_min === "number" ? q.number_min : Number.MIN_SAFE_INTEGER;
          const maxVal = typeof q.number_max === "number" ? q.number_max : Number.MAX_SAFE_INTEGER;
          s = z.preprocess(
            (value) => (value === "" ? undefined : value),
            z.coerce.number().int().min(minVal).max(maxVal),
          );
        } else if (q.question_type === "stars") {
          const minVal = typeof q.stars_min === "number" ? q.stars_min : 1;
          const maxVal = typeof q.stars_max === "number" ? q.stars_max : 5;
          s = z.preprocess(
            (value) => (value === "" ? undefined : value),
            z.coerce.number().int().min(minVal).max(maxVal),
          );
        } else if (q.question_type === "yes_no") {
          s = z.enum(["yes", "no"]);
        } else if (q.question_type === "yes_no_with_explanation") {
          s = z.string().refine((value) => value === "no" || value === "yes" || value.startsWith("yes|"));
        } else if (q.question_type === "checkbox") {
          s = q.is_required ? z.literal("true") : z.enum(["true", "false"]);
        } else if (q.question_type === "signature") {
          s = z.string().min(1);
        } else {
          s = z.string();
        }

        return [
          fieldName,
          q.is_required
            ? s.refine((value) => typeof value !== "string" || value.trim().length > 0)
            : z.preprocess((value) => (value === "" ? undefined : value), s.optional()),
        ];
      }),
    ),
  );
}

/**
 * Process form data and extract answers array for API submission
 */
export function processFormDataToAnswers(formData: FormData) {
  const answersArray: Array<{
    product_question_group_question_guid: string;
    answer_value?: number;
    answer_text?: string;
  }> = [];

  // Build a quick lookup for types provided via hidden inputs
  const typeByGuid = new Map<string, string>();
  for (const [key, value] of formData.entries()) {
    if (key.startsWith("type-")) {
      const guid = key.replace("type-", "");
      typeByGuid.set(guid, String(value));
    }
  }

  for (const [key, value] of formData.entries()) {
    if (!key.startsWith("answer-")) continue;
    const question_guid = key.replace("answer-", "").replace("[]", "");
    const qtype = typeByGuid.get(question_guid);
    const valStr = String(value);
    if (valStr === "") continue;

    const answer: {
      product_question_group_question_guid: string;
      answer_value?: number;
      answer_text?: string;
    } = { product_question_group_question_guid: question_guid };

    if (qtype === "yes_no" || qtype === "checkbox") {
      // Map typical values to 0/1
      const normalized = valStr.toLowerCase();
      answer.answer_value =
        normalized === "yes" || normalized === "true" || normalized === "1" || normalized === "on" ? 1 : 0;
    } else if (qtype === "yes_no_with_explanation") {
      // Parse "yes|text" or "yes|" or "no"
      if (valStr.startsWith("yes")) {
        answer.answer_value = 1;
        // Extract text after the pipe if present
        const pipeIndex = valStr.indexOf("|");
        if (pipeIndex !== -1 && pipeIndex < valStr.length - 1) {
          const explanationText = valStr.substring(pipeIndex + 1).trim();
          if (explanationText) {
            answer.answer_text = explanationText;
          }
        }
      } else {
        answer.answer_value = 0;
      }
    } else if (qtype === "number" || qtype === "stars") {
      if (valStr === "") continue;
      else {
        const parsed = Number(valStr);
        answer.answer_value = Number.isFinite(parsed) ? parsed : undefined;
      }
    } else {
      if (valStr === "") continue;
      // freetext/signature/select_with_custom (or unknown) -> use text
      answer.answer_text = valStr;
    }

    answersArray.push(answer);
  }

  return answersArray;
}
